// Slot content lives under .tox, whose skin reset (.tox *:not(svg):not(rect) at
// specificity 0,1,2) defeats single-class rules: .templateSelect's width, the
// react-select control's width, and react-select's visually-hidden technique on its
// screen-reader guidance. Each is re-asserted here under a two-class scope, so the
// sales-card footer — outside .tox — keeps shipping its own untouched rules.
.toolbarEnd {
  // `.tox` fixes 16px and the reset makes every descendant inherit it, so the control's text
  // would out-size its menu — that portals to the body, at the app's body size. Re-pointed here
  // to the same token the menu reads, so the two match in any app.
  .templateSelect {
    width: 15rem;
    font-size: var(--bs-body-font-size);
  }

  // Pin the control to its fixed wrapper: without this a picked template's name
  // stretches the control (its width falls back to auto) and the slot edge moves
  // after the toolbar has measured. react-select__control is the stable
  // classNamePrefix hook, not a hashed emotion class.
  .templateSelect :global(.react-select__control) {
    width: 100%;
  }

  // react-select's A11yText span (emotion `label:a11yText`) hides via
  // position/1px/clip; the reset flips it to static/auto and the guidance paints
  // as a paragraph above the control and inside open menus. Re-assert its exact
  // technique (minus z-index: an off-screen 1px span needs no stacking).
  .templateSelect [class*='a11yText'] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);
    white-space: nowrap;
    border: 0;
  }

  // react-select's single-value carries its own max-width/overflow/white-space (an emotion
  // class of specificity 0,1,0); the reset's `max-width: none` and inherited `white-space:
  // normal` beat it, so a long picked template name wraps to a second line inside the 38px
  // control. Re-assert under this two-class scope.
  .templateSelect :global(.react-select__single-value) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: calc(100% - 8px);
  }

  // react-select's classNamePrefix toggles `--is-focused` on the control regardless of the
  // `styles` prop, but the resulting emotion class carries the same fixed resting `border` the
  // shared Select always renders (libs/webui/input/src/Select/index.tsx's `mergeStyle.control`)
  // — specificity 0,1,0, same as every other emotion class the reset (0,1,2) already beats here.
  // Re-assert a 1px solid border using the resting border's own tokens so a keyboard-focused
  // template select reads the same focus affordance as the rail's Assignee select.
  .templateSelect :global(.react-select__control--is-focused) {
    border: var(--bs-border-width) solid var(--bs-link-color);
  }
}

// The same skin-reset trap (`.tox *:not(svg):not(rect)` at specificity 0,1,2) strips every
// `.btn` inside the editor header, so it is re-asserted here, shared by the inbox toolbar and
// both modals alike. The re-assertion reads each button's OWN `--bs-btn-*` tokens, so a
// link-variant control (Refresh, Formalize, Attach) reads chromeless — transparent fill,
// `--bs-link-color` text — while the inbox's filled Send and Add note read filled, with no
// separate hand-rolled override.
.toolbarEnd :global(.btn) {
  background-color: var(--bs-btn-bg);
  border: var(--bs-btn-border-width) solid var(--bs-btn-border-color);
  color: var(--bs-btn-color);
  padding: var(--bs-btn-padding-y) var(--bs-btn-padding-x);
  font-size: var(--bs-btn-font-size);
  line-height: var(--bs-btn-line-height);
}

.toolbarEnd :global(.btn:hover) {
  color: var(--bs-btn-hover-color);
  background-color: var(--bs-btn-hover-bg);
  border-color: var(--bs-btn-hover-border-color);
}

.toolbarEnd :global(.btn:active) {
  color: var(--bs-btn-active-color);
  background-color: var(--bs-btn-active-bg);
  border-color: var(--bs-btn-active-border-color);
}

.toolbarEnd :global(.btn:focus-visible) {
  color: var(--bs-btn-hover-color);
  border-color: var(--bs-btn-hover-border-color);
  outline: 0;
  box-shadow: var(--bs-btn-focus-box-shadow);
}

.toolbarEnd :global(.btn:disabled) {
  color: var(--bs-btn-disabled-color);
  background-color: var(--bs-btn-disabled-bg);
  border-color: var(--bs-btn-disabled-border-color);
}

// The same trap strips every `.spinner-border` inside the editor header down to a blank,
// zero-size span: `border: 0`, `width: auto` and `height: auto` beat Bootstrap's own rule, and
// `display` is left at the reset's `static`/inline default, so even a restored width has nothing
// to apply to. That's why the wand vanishes while formalizing and Send's spinner never shows.
// Bootstrap's own --bs-spinner-* custom properties are untouched (the reset only strips literal
// properties, not custom ones), so re-asserting the literal properties here just reads them back —
// shared by the inbox toolbar and both modals' composer toolbars alike.
.toolbarEnd :global(.spinner-border),
.toolbarEnd :global(.spinner-border-sm) {
  display: inline-block;
  width: var(--bs-spinner-width);
  height: var(--bs-spinner-height);
  vertical-align: var(--bs-spinner-vertical-align);
  border: var(--bs-spinner-border-width) solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: var(--bs-spinner-animation-speed) linear infinite var(--bs-spinner-animation-name);
}

// The wand's idle state (icon alone) and busy state (spinner + BusyButton's single-space
// `busyText`) render at different natural widths, so — even with the spinner now visible above —
// the slot still shifts on formalize. A fixed width, the same fix `.composerSendButton` uses for
// Send/Add Note (ConversationDetail.module.scss), keeps the slot's edge still. Targeted by its own
// `aria-label` (FormalizeButton.tsx) rather than a new className, since the button is a shared
// `libs/webui/data-tools` component with no style hook of its own.
.toolbarEnd :global([aria-label='Formalize with AI']) {
  width: 2rem;
}

// The record-page composer's plain Send and the Add note that replaces it in Note mode share this
// fixed width, so busy never shifts every other header control and a mode switch never moves the
// slot's edge — the same fix the inbox's `.composerSendButton` uses for its filled Send and
// Add note (libs/chat/webui/src/Inbox/ConversationDetail.module.scss), at this mount's own 7rem.
// Two classes outrank the skin reset's `width: auto` the way the shared end-slot rules above do.
.toolbarEnd .sendButton {
  width: 7rem;
}
